import { useQueries, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import { getProviderProfile } from '@/features/provider-directory/api/providerApi';
import type { CareStageCardData } from '@/features/chatbot/components/understandCareStageData';
import type {
  FpQaHubResource,
  FpQaResourceBundle,
} from '@/shared/components/navigator-results';

// Re-exported from the shared navigator-results module so existing FP-feature
// imports keep resolving while the canonical definitions live in shared (the
// careseeker shared-care-guide view reuses the same Key Resources bundle).
export type { FpQaHubResource, FpQaResourceBundle };

const QA_PROVIDER_QUERY_PREFIX = ['fp-qa', 'provider'] as const;
const QA_RESOURCES_QUERY_PREFIX = ['fp-qa', 'care-stage-resources'] as const;
const QA_HUB_RESOURCES_QUERY_PREFIX = ['fp-qa', 'hub-resources'] as const;

/**
 * Hydrates a list of provider ids in parallel via the existing
 * GET /providers/:id endpoint. Returns the per-provider react-query results
 * so the caller can render loading/error states per card.
 */
export function useFpQaProviderProfiles(providerIds: string[]) {
  return useQueries({
    queries: providerIds.map((id) => ({
      queryKey: [...QA_PROVIDER_QUERY_PREFIX, id],
      queryFn: () => getProviderProfile(id),
      enabled: Boolean(id),
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    })),
  });
}

/**
 * Hub resources (resource_hub articles) mapped to the engagement's care
 * stage. Same source as the PDF renderer, so the in-app Key Resources
 * panel and the PDF surface identical lists. Hub resources have seoUrls
 * (unlike consideration cards) so each one deep-links to
 * /resource-hub-detail/:seoUrl.
 */
export function useFpQaHubResources(engagementId: string | null | undefined) {
  return useQuery({
    queryKey: [...QA_HUB_RESOURCES_QUERY_PREFIX, engagementId ?? ''],
    queryFn: async () => {
      const { data } = await apiClient.get<FpQaResourceBundle>(
        `/financial-advisors/me/qa-engagements/${engagementId}/hub-resources`,
      );
      return {
        specialtyResourcesBySpecialtyId: data?.specialtyResourcesBySpecialtyId ?? {},
        careStageResources: data?.careStageResources ?? [],
      };
    },
    enabled: Boolean(engagementId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useFpQaCareStageResources(careStageId: string | null | undefined) {
  return useQuery({
    queryKey: [...QA_RESOURCES_QUERY_PREFIX, careStageId ?? ''],
    queryFn: async () => {
      // Backend returns { overviewNarrative, cards: CareStageCardData[] }.
      // We only need the cards array here.
      const { data } = await apiClient.get<{
        overviewNarrative: string | null;
        cards: CareStageCardData[];
      }>('/resources/care-stage-consideration-cards', {
        params: { care_stage_id: careStageId },
      });
      return Array.isArray(data?.cards) ? data.cards : [];
    },
    enabled: Boolean(careStageId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}
